import { CountryRole, GlobalRole, OrganizationRole, Role } from './role';
import { Deletable } from '../interface/deletable';
import { Identifiable } from '../interface/identifiable';
import { Factory } from '../interface/factory';
import { CountryCode } from '../enum/country-code';
import { Email } from './email';
import { Phone } from './phone';
import { MothershipData } from './mothership-data';

export class PostalAddress {
  address: string;
  sublocality?: string;
  postcode: string;
  city: string;
  province?: string;
  country: string;

  constructor(json: any) {
    this.address = json.address;
    this.sublocality = json.sublocality ? json.sublocality : undefined;
    this.postcode = json.postcode;
    this.city = json.city;
    this.province = json.province ? json.sublocality : undefined;
    this.country = json.country;
  }
}

export class DefaultPaymentMethod {
  id: string;
  organizationId: string;
  modified: Date;

  constructor(json: any) {
    this.id = json.id;
    this.organizationId = json.organizationId;
    this.modified = json.modified ? new Date(json.modified) : new Date();
  }
}

export enum ConsentType {
  AllowProductAndServiceOffers = 'AllowProductAndServiceOffers',
}

export class Consent {
  opt: boolean;

  constructor(json: any) {
    this.opt = json.opt ? Boolean(json.opt) : false;
  }
}

export class User implements Deletable, Identifiable {
  id: string;
  aliasId?: string;
  alias?: Alias[];

  deleted?: boolean;
  deleteAfter?: Date;

  test?: boolean;

  consent?: Record<string, Consent>;

  firstName: string;
  preferredName?: string;
  middleNames?: string;
  lastName: string;

  nids?: string[];
  dob?: string;

  roles: Role[];

  email?: Email;
  phone?: Phone;
  invoiceEmail?: string;
  postalAddress: PostalAddress;
  deliveryAddress?: PostalAddress;

  defaultPaymentMethodId?: string;
  defaultPaymentMethods: DefaultPaymentMethod[];

  countries: string[];
  mothershipData?: MothershipData;

  /** When the user last confirmed their profile details (for example after an in-app prompt). */
  lastVerified?: Date;

  created: Date;
  modified: Date;

  constructor(json: any) {
    this.id = json.id;

    this.aliasId = json.aliasId;
    this.alias = json.alias ? json.alias.map((a: any) => new Alias(a)) : [];
    this.deleted = json.deleted ? Boolean(json.deleted) : false;
    this.deleteAfter = json.deleteAfter ? new Date(json.deleteAfter) : undefined;

    this.test = json.test ? Boolean(json.test) : false;

    this.consent = json.consent
      ? Object.keys(json.consent).reduce((acc: Record<string, Consent>, key: string) => {
          acc[key] = new Consent(json.consent[key]);
          return acc;
        }, {})
      : undefined;

    this.firstName = json.firstName;
    this.preferredName = json.preferredName ? json.preferredName : undefined;
    this.middleNames = json.middleNames ? json.middleNames : undefined;
    this.lastName = json.lastName;

    this.nids = json.nids ? json.nids : [];
    this.dob = json.dob ? json.dob : undefined;

    const roleFactory: Factory<Role> = Role.getFactory();
    this.roles = json.roles ? json.roles.map((json: any) => roleFactory.make(json)) : [];

    const emailFactory: Factory<Email> = Email.getFactory();
    this.email = json.email ? emailFactory.make(json.email) : undefined;

    const phoneFactory: Factory<Phone> = Phone.getFactory();
    this.phone = json.phone ? phoneFactory.make(json.phone) : undefined;

    this.invoiceEmail = json.invoiceEmail ? json.invoiceEmail : undefined;

    this.postalAddress = new PostalAddress(json.postalAddress);
    this.deliveryAddress = json.deliveryAddress ? new PostalAddress(json.deliveryAddress) : undefined;
    this.defaultPaymentMethodId = json.defaultPaymentMethodId ? json.defaultPaymentMethodId : undefined;
    this.defaultPaymentMethods = json.defaultPaymentMethods
      ? json.defaultPaymentMethods.map((json: any) => new DefaultPaymentMethod(json))
      : [];

    // This is a list of countries to which the user is attached (e.g. has a product in).
    this.countries = json.countries ?? [];
    this.mothershipData = json.mothershipData ? new MothershipData(json.mothershipData) : undefined;

    this.lastVerified = json.lastVerified ? new Date(json.lastVerified) : undefined;

    this.created = new Date(json.created);
    this.modified = new Date(json.modified);
  }

  public static getFactory(): Factory<User> {
    return new (class implements Factory<User> {
      make(json: any): User {
        return new User(json);
      }

      getTableName(): string {
        return 'users';
      }
    })();
  }

  static getUrl(userId?: string): string;
  static getUrl(userId: string): string {
    return '/users' + (userId ? '/' + userId : '');
  }

  hasConsent(organizationId: string, type: ConsentType): boolean {
    const key = `${organizationId}_${type}`;
    const consent = this.consent ? this.consent[key] : undefined;

    if (consent) {
      return consent.opt;
    }

    switch (type) {
      case ConsentType.AllowProductAndServiceOffers:
        return true; // default value for this consent type
      default:
        return false; // default value for other consent types
    }
  }

  getAliasId(organizationId: string): string | undefined {
    const alias = this.alias?.find((a) => a.organizationId === organizationId);
    return alias?.id;
  }

  getName(showMiddleName: boolean = false): string {
    return showMiddleName && this.middleNames
      ? this.firstName.trim() + ' ' + this.middleNames.trim() + ' ' + this.lastName.trim()
      : this.firstName.trim() + ' ' + this.lastName.trim();
  }

  getAbbriviatedName(): string {
    return this.firstName.charAt(0) + '. ' + this.lastName;
  }

  getRolesByCountry(code: CountryCode): Role[] {
    return this.roles.filter(
      (r) => ((r instanceof CountryRole && r.code === code) || r instanceof GlobalRole) && r.scopes > 0,
    );
  }

  getAllUserRoles(): Role[] {
    return this.roles;
  }

  getUserOrganizationRoles(): Role[] {
    return this.roles.filter((r) => r instanceof OrganizationRole);
  }

  getNidByCountry(code: CountryCode): string {
    return this.nids?.find((n) => n.startsWith(code))?.slice(2) ?? '';
  }

  userIsMemberOfCountry(code: CountryCode): boolean {
    return this.postalAddress.country === code;
  }

  userIsMemberOfMultipleOrganizations() {
    // check in user alias

    if (this.alias && this.alias.length > 1) {
      return this.alias;
    } else {
      return false;
    }
  }
}

export class Alias {
  id: string;
  organizationId: string;

  constructor(json: any) {
    this.id = json.id;
    this.organizationId = json.organizationId;
  }
}
